import React from 'react';
import Select from 'react-select';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo } from '@fortawesome/free-solid-svg-icons';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/tooltip';
import type {
  LeadUnit,
  UpcomingSchedule,
  UpcomingStageSettings,
} from '../../types/flowStage';

interface UpcomingScheduleSectionProps {
  settings: UpcomingStageSettings;
  value?: UpcomingSchedule;
  onChange: (schedule: UpcomingSchedule) => void;
  canWrite?: boolean;
}

const UNIT_LABELS: Record<LeadUnit, string> = {
  minutes: 'Minutes',
  hours: 'Hours',
  days: 'Days',
  weeks: 'Weeks',
};

const DEFAULT_LEAD: UpcomingSchedule['lead'] = { amount: 1, unit: 'days' };

// Render menus above the drawer's overflow containers.
const menuPortalProps = {
  menuPortalTarget: typeof document !== 'undefined' ? document.body : undefined,
  menuPosition: 'fixed' as const,
  styles: { menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) },
};

/** Whether a schedule can be saved: a date is chosen. The API validates the rest. */
export const isUpcomingScheduleComplete = (
  settings: UpcomingStageSettings,
  schedule?: UpcomingSchedule | null,
): boolean => settings.anchors.some((a) => a.value === schedule?.anchor);

/**
 * Schedule of an upcoming stage: which date it counts back from, how long before,
 * and whether occurrences found late still get it. When its messages go out is set
 * on each trigger. The options come from the API (`flowStageType.upcoming`), which
 * also validates the saved schedule.
 */
export const UpcomingScheduleSection: React.FC<UpcomingScheduleSectionProps> = ({
  settings,
  value,
  onChange,
  canWrite = true,
}) => {
  const lead = value?.lead ?? DEFAULT_LEAD;

  // The date is never guessed: it stays unset until staff pick one.
  const update = (patch: Partial<UpcomingSchedule>) => {
    onChange({ lead, catchUp: value?.catchUp ?? false, ...value, ...patch });
  };

  const anchorOptions = settings.anchors.map((a) => ({ value: a.value, label: a.label }));
  const unitOptions = settings.leadUnits.map((unit) => ({ value: unit, label: UNIT_LABELS[unit] }));

  return (
    <div className="flex flex-col gap-3 rounded-xl border border-zinc-200 bg-white px-4 py-3">
      <div className="text-base font-bold text-[#8a6a2c]">Schedule</div>

      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span>Fire</span>
        <input
          type="number"
          min={0}
          step={1}
          value={lead.amount}
          onChange={(e) =>
            update({ lead: { ...lead, amount: Math.max(0, Math.floor(Number(e.target.value) || 0)) } })
          }
          disabled={!canWrite}
          className="w-20 rounded border px-2 py-1"
          aria-label="Lead amount"
        />
        <div className="w-32">
          <Select
            value={unitOptions.find((o) => o.value === lead.unit) ?? null}
            onChange={(selected) => {
              if (selected) update({ lead: { ...lead, unit: selected.value } });
            }}
            options={unitOptions}
            isDisabled={!canWrite}
            isSearchable={false}
            {...menuPortalProps}
          />
        </div>
        <span>before the</span>
        <div className="min-w-64 flex-1">
          <Select
            value={anchorOptions.find((o) => o.value === value?.anchor) ?? null}
            onChange={(selected) => {
              if (selected) update({ anchor: selected.value });
            }}
            options={anchorOptions}
            placeholder="Choose a date…"
            isDisabled={!canWrite}
            isSearchable={false}
            {...menuPortalProps}
          />
        </div>
      </div>
      <p className="text-xs text-zinc-500">
        Days and weeks fire at midnight that many days before; minutes and hours count from
        the booking&apos;s time. Set when each message goes out on its trigger, for example at
        6:00 PM.
      </p>

      <div className="flex items-center gap-2 text-sm">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={value?.catchUp ?? false}
            onChange={(e) => update({ catchUp: e.target.checked })}
            disabled={!canWrite}
          />
          Catch up late events
        </label>
        <Tooltip delayDuration={200}>
          <TooltipTrigger asChild>
            <span className="cursor-help text-zinc-400" aria-label="About catching up late events">
              <FontAwesomeIcon icon={faCircleInfo} className="h-3.5 w-3.5" />
            </span>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-xs">
            When a booking or renewal shows up after the stage would have fired for it (say, a
            tour booked 2 days out on a 5-days-before stage), fire right away, as long as its
            date hasn't passed. Off: those are skipped. On a newly saved stage this also covers
            everything already past that point.
          </TooltipContent>
        </Tooltip>
      </div>
    </div>
  );
};

export default UpcomingScheduleSection;
